import { AppPresence, WidgetFormSubmissionWithWidget } from "@closerplatform/spinner-openapi";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { PickerWidgetMessages } from "@fidget/common/models/pickerWidgetMessages";
import { observer } from "mobx-react";
import React from "react";
import { userActions } from "../../actions/user";
import { useForeContext, useStore } from "../../models";
import {
  MessagePickerWidget,
  MessageWithCallback
} from "./MessagePickerWidget/MessagePickerWidget";
import { MessageWidgetForm } from "./messageWidgetForm/messageWidgetForm";

interface Props {
  messageId: string;
  text?: string;
  alignRight: boolean;
  withCallButton?: boolean;
  scrollToBottom?: () => void;
  withCobrowsingDialog?: boolean;
  submission?: WidgetFormSubmissionWithWidget;
  pickerWidgetMessages?: PickerWidgetMessages;
  question?: string;
}

export const MessageWidgets = observer(
  ({
    text,
    alignRight,
    messageId,
    withCallButton,
    scrollToBottom,
    withCobrowsingDialog,
    submission,
    pickerWidgetMessages,
    question
  }: Props) => {
    const { foreCallStore } = useStore();
    const context = useForeContext();
    const { callToNewAssigneeMessage } = context;

    const isAgentNotAvailable =
      context.state.shared.assignedAgent === undefined ||
      context.state.shared.assignedAgent.presence !== AppPresence.Available;

    const createCallingCallbackMessage = (message: string): MessageWithCallback => {
      return {
        message,
        callback: handleCall,
        disabled: isAgentNotAvailable,
        primary: true
      };
    };

    const constructMessagesWithCallback = (messages: [string]): MessageWithCallback[] => {
      const isCallingMessage = (actualMessage: string, desiredMessage?: string) => {
        return desiredMessage !== undefined && actualMessage === desiredMessage;
      };

      return messages.map((message) => {
        if (isCallingMessage(message, callToNewAssigneeMessage)) {
          return createCallingCallbackMessage(message);
        }

        return {
          message,
          callback: () => sendMessage(message)
        };
      });
    };

    const renderButtons = () => {
      if (!alignRight) {
        if (withCallButton) {
          return renderCallButton();
        } else if (withCobrowsingDialog) {
          return renderCobrowsingDialog();
        } else if (pickerWidgetMessages && question) {
          return renderMessagePickerWidget(pickerWidgetMessages[question]!);
        }
      }
      return <></>;
    };

    const sendMessage = (message: string) => {
      userActions.sendButtonClicked(context, message);
      if (scrollToBottom !== undefined) {
        scrollToBottom();
      }
    };

    const handleCall = () => {
      foreCallStore.reloadGrantedPermissions().then(() => {
        foreCallStore.connect(context.state.shared.assignedAgent!.id);
      });
    };

    const renderCallButton = () => {
      const acceptMessage = intl.formatMessage("richMessages.callProposal.callButtonWidget");
      const messagesWithCallbacks = [createCallingCallbackMessage(acceptMessage)];
      return <MessagePickerWidget messages={messagesWithCallbacks} />;
    };

    const renderMessagePickerWidget = (messages: [string]) => {
      const messagesWithCallbacks = constructMessagesWithCallback(messages);
      return messagesWithCallbacks && <MessagePickerWidget messages={messagesWithCallbacks} />;
    };

    const startCobrowsingSession = (message: string) => {
      userActions.sendCobrowsingAcceptation(context, message);
      if (scrollToBottom !== undefined) {
        scrollToBottom();
      }
    };

    const renderCobrowsingDialog = () => {
      const allowMessage = intl.formatMessage("widget.cobrowsingDialog.allow");
      const denyMessage = intl.formatMessage("widget.cobrowsingDialog.deny");
      const isNoCall = foreCallStore && foreCallStore.isNoCall;
      const shouldDisable = isAgentNotAvailable ? isNoCall : isAgentNotAvailable;
      const messagesWithCallbacks: MessageWithCallback[] = [
        { message: denyMessage, callback: () => sendMessage(denyMessage), disabled: shouldDisable },
        {
          message: allowMessage,
          callback: () => startCobrowsingSession(allowMessage),
          disabled: shouldDisable,
          primary: true
        }
      ];
      return <MessagePickerWidget messages={messagesWithCallbacks} />;
    };

    const renderWidgets = () => {
      return submission ? (
        <MessageWidgetForm
          scrollToBottom={scrollToBottom}
          messageId={messageId}
          submission={submission}
        />
      ) : (
        renderButtons()
      );
    };

    return renderWidgets();
  }
);
